import { HomeOutlined } from "@ant-design/icons";
import { Breadcrumb, Space, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { useLoaderData } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import {
  CustomTable,
  CustomTextField,
  AvailabilityStatusTag,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { PrecomputedAmenityData } from "~/types";
import { ROUTES, displayReservationPolicy, getAmenityStatus } from "~/utils";
import { AmenityTablePageLoaderType } from "./loaders";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import i18n from "~/i18n";
import { useAppTranslation } from "~/hooks/useAppTranslation";

export const getAmenityTableColumns =
  (): ColumnsType<PrecomputedAmenityData> => [
    {
      title: i18n.t("Name"),
      dataIndex: ["formattedAmenityDetails", "amenityDetails", "name"],
      key: "name",
      render: (_, record: PrecomputedAmenityData) => {
        const id = record.formattedAmenityDetails.amenityDetails.id;
        return (
          <Typography.Link href={`/amenity/${id}`}>
            {record.formattedAmenityDetails.amenityDetails.name}
          </Typography.Link>
        );
      },
      width: 200,
    },
    {
      title: i18n.t("Building"),
      key: "buildingName",
      width: 250,
      render: (_, record: PrecomputedAmenityData) => {
        const buildingName =
          record.formattedAmenityDetails.buildingDetails?.name ?? "";
        return <CustomTextField value={buildingName} />;
      },
    },
    // TODO: may be nice to show next open/close time, either in separate columns or
    //   on hover of the status
    {
      title: i18n.t("Status"),
      key: "status",
      width: 200,
      sorter: (a, b) =>
        getAmenityStatus(a)[0].length - getAmenityStatus(b)[0].length,
      sortDirections: ["descend", "ascend"],
      render: (_, record: PrecomputedAmenityData) => {
        const status = getAmenityStatus(record);
        return <AvailabilityStatusTag status={status[0]} />;
      },
    },
    {
      title: "Reservation Policy",
      key: "reservationPolicy",
      width: 250,
      render: (_, record: PrecomputedAmenityData) => {
        const policy =
          record.formattedAmenityDetails.amenityDetails.reservationRule.policy;
        return <CustomTextField value={displayReservationPolicy(policy)} />;
      },
    },
  ];

export const AmenityTablePage = () => {
  const loaderData = useLoaderData() as AmenityTablePageLoaderType;
  const { t } = useAppTranslation();
  const { isTableLoading } = useRouteActivity();

  if (loaderData.amenityList.error) {
    console.error("amenity page", loaderData.amenityList.error);
  }

  const [columns, setColumns] = useState(getAmenityTableColumns());

  useEffect(() => {
    const now = dayjs();
    const toExactMinute = 1000 - now.millisecond() + (60 - now.second()) * 1000;
    let timer: ReturnType<typeof setInterval> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    timeout = setTimeout(() => {
      timer = setInterval(() => {
        setColumns(getAmenityTableColumns());
      }, 1000 * 60); // every minutes
      setColumns(getAmenityTableColumns());
    }, toExactMinute);
    return () => {
      if (timeout !== undefined) clearTimeout(timeout);
      if (timer !== undefined) clearInterval(timer);
    };
  });

  return (
    <>
      <Breadcrumb
        separator=">"
        items={[
          {
            key: "home",
            title: <HomeOutlined />,
            href: ROUTES.home,
          },
          {
            key: "amenity",
            title: "Amenity",
          },
        ]}
      />
      <section style={{ marginTop: "2rem" }}>
        <CustomTable
          tableHeaders={
            <TableHeaderBar
              left={
                <TableHeaderInfo
                  title="All Amenity Info"
                  count={loaderData.amenityList.data.length}
                  countLabel={t("Total_Records_Found")}
                />
              }
            />
          }
          mobileCardConfig={{ titleKey: "name", primaryKeys: ["status"] }}
          tableProps={{
            dataSource: loaderData.amenityList.data || [],
            columns,
            loading: isTableLoading,
          }}
        />
      </section>
    </>
  );
};
